import React from 'react';
import { BaseDrawer } from '@/components';
import { DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerOverlay } from '@/components/ui/drawer';
import { AccordionSection } from './AccordionSection';
import { MultiCheckbox } from './MultiCheckbox';
import { isInitialState } from './utils';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FilterOption, FilterData, FiltersValues } from './types';
import { Icon } from '@/components/shared/display/Icon';
import { CloseIcon } from '@/assets/Icons';

interface AllFiltersDrawerContentProps {
  allFilters: FilterData[];
  currentFiltersValues: FiltersValues;
  initialFiltersValues: FiltersValues;
  handleFiltersValuesChange: (filtersValues: FiltersValues) => void;
  handleResetClick: () => void;
  handleDrawerOpenChange: (isOpened: boolean) => void;
  drawerCloseBtnText?: string;
  customCounterRender?: (count: number) => React.ReactNode;
  customItemRender?: (option: FilterOption) => React.ReactNode;
}

const AllFiltersDrawerContent = ({
  allFilters,
  currentFiltersValues,
  handleFiltersValuesChange,
  handleResetClick,
  handleDrawerOpenChange,
  initialFiltersValues,
  drawerCloseBtnText,
  customItemRender,
  customCounterRender,
}: AllFiltersDrawerContentProps) => {
  return (
    <>
      <DrawerOverlay className="fixed inset-0 bg-black/40" />
      <DrawerContent
        className="bg-background w-[var(--notifications-drawer-width)] rounded-none h-full flex flex-col"
        data-vaul-no-drag
      >
        <DrawerHeader className="flex items-center justify-between px-6 py-4 border-b border-[#B1B9C3] dark:border-slate-600">
          <DrawerTitle className="font-medium dm-text-primary">All Filters</DrawerTitle>
          <DrawerClose>
            <Icon icon={CloseIcon} />
          </DrawerClose>
        </DrawerHeader>

        <div className="px-4 overflow-y-auto">
          {allFilters.map((filter) => {
            const content =
              filter.filterType === 'multicheckbox' ? (
                <div className="flex flex-col gap-2">
                  <MultiCheckbox
                    filterOptions={filter.filterOptions}
                    filtersValues={currentFiltersValues}
                    handleFiltersValuesChange={handleFiltersValuesChange}
                    filterLabel={filter.filterLabel}
                    customItemRender={customItemRender}
                    customCounterRender={customCounterRender}
                    className="p-2 rounded-xl"
                  />
                </div>
              ) : (
                <></>
              );
            return (
              <div key={filter.filterLabel} className="mt-3 pb-3 border-b dark:border-slate-700">
                {filter.triggerType === 'accordeon' || filter.placement === 'quick' ? (
                  <AccordionSection label={filter.filterLabel} content={content} defaultOpen />
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="flex justify-between items-center w-full py-5 px-6 mt-auto">
          <div
            className={cn(
              'font-medium cursor-pointer hover:opacity-80 dm-text-primary',
              !isInitialState(initialFiltersValues, currentFiltersValues) ? 'visible' : 'invisible'
            )}
            onClick={handleResetClick}
          >
            Reset
          </div>

          {!!drawerCloseBtnText && (
            <Button variant="filled" size="lg" className="px-4" onClick={() => handleDrawerOpenChange(false)}>
              {drawerCloseBtnText}
            </Button>
          )}
        </div>
      </DrawerContent>
    </>
  );
};

interface AllFiltersDrawerProps {
  isVisible: boolean;
  children: React.ReactNode;
  handleOpenChange: (isVisible: boolean) => void;
  allFilters: FilterData[];
  currentFiltersValues: FiltersValues;
  handleFiltersValuesChange: (filtersValues: FiltersValues) => void;
  handleResetClick: () => void;
  initialFiltersValues: FiltersValues;
  drawerCloseBtnText?: string;
  customItemRender?: (option: FilterOption) => React.ReactNode;
  customCounterRender?: (count: number) => React.ReactNode;
}

export const AllFiltersDrawer = ({
  handleOpenChange,
  children,
  isVisible,
  allFilters,
  currentFiltersValues,
  handleFiltersValuesChange,
  handleResetClick,
  initialFiltersValues,
  drawerCloseBtnText,
  customItemRender,
  customCounterRender,
}: AllFiltersDrawerProps) => {
  return (
    <BaseDrawer
      onOpenChange={handleOpenChange}
      customContent={
        <AllFiltersDrawerContent
          allFilters={allFilters}
          currentFiltersValues={currentFiltersValues}
          handleFiltersValuesChange={handleFiltersValuesChange}
          handleResetClick={handleResetClick}
          handleDrawerOpenChange={handleOpenChange}
          initialFiltersValues={initialFiltersValues}
          drawerCloseBtnText={drawerCloseBtnText}
          customItemRender={customItemRender}
          customCounterRender={customCounterRender}
        />
      }
      direction="right"
      isOpen={isVisible}
    >
      {children}
    </BaseDrawer>
  );
};
